import { Rectangle } from 'recharts';
import type { ComponentProps } from 'react';

import { CHART_BAR_FALLBACK, CHART_DIMENSIONS } from '../shared/chart-tokens';
import { colorForSeries } from '../shared/chart-palette';
import { resolveFallbackFill } from '../shared/chart-helpers';

type RectangleProps = ComponentProps<typeof Rectangle>;

export interface BarChartBarProps extends RectangleProps {
  value: number | null;
  seriesIndex: number;
  seriesCount: number;
}

// [r, r, 0, 0] rounds the value-end corners for both positive and negative
// bars because Recharts inverts path traversal for negative heights.
const BAR_RADIUS: [number, number, number, number] = [
  CHART_DIMENSIONS.barCornerRadius,
  CHART_DIMENSIONS.barCornerRadius,
  0,
  0,
];

export const BarChartBar = (props: BarChartBarProps) => {
  const { value, seriesIndex, seriesCount, ...rectangleProps } = props;
  const { width, height, y } = rectangleProps;

  if (width === undefined || height === undefined || y === undefined) {
    return null;
  }

  if (value === null || value === 0) {
    const thickness = CHART_BAR_FALLBACK.thicknessPx;
    const { fill, fillOpacity } = resolveFallbackFill(
      value,
      seriesIndex,
      seriesCount
    );

    return (
      <Rectangle
        {...rectangleProps}
        y={y - thickness}
        height={thickness}
        fill={fill}
        fillOpacity={fillOpacity}
        radius={BAR_RADIUS}
      />
    );
  }

  if (width === 0 || height === 0) {
    return null;
  }

  const fill = colorForSeries(seriesIndex, seriesCount);

  return <Rectangle {...rectangleProps} fill={fill} radius={BAR_RADIUS} />;
};
